/**
 * Discount & Coupon API Service Client
 * Endpoint: POST /api/discounts/validate
 * Conforms to FastAPI backend: backend/app/api/routes/discounts.py
 */

import { SupportedCurrency } from '../types';
import { sanitizeCurrency } from '../utils/currency';

export interface DiscountValidateRequest {
  code: string;
  total_vbucks: number;
  currency: SupportedCurrency;
  customer_username?: string;
  customer_account_id?: string;
}

export interface DiscountValidateResponse {
  valid: boolean;
  code: string;
  discount_pct: number;
  discount_amount: number;
  base_price: number;
  final_price: number;
  currency: SupportedCurrency;
  message: string;
}

export interface ValidateCouponOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}

export class DiscountApiError extends Error {
  public status?: number;
  public detail?: string;

  constructor(message: string, status?: number, detail?: string) {
    super(message);
    this.name = 'DiscountApiError';
    this.status = status;
    this.detail = detail;
  }
}

/**
 * Validates a discount coupon code against the FastAPI backend.
 * Handles timeouts, rate limits (HTTP 429), expired coupons (HTTP 400),
 * min vbucks requirements (HTTP 400), and missing coupons (HTTP 404).
 */
export async function validateCoupon(
  params: DiscountValidateRequest,
  options: ValidateCouponOptions = {}
): Promise<DiscountValidateResponse> {
  const cleanCode = params.code?.trim().toUpperCase();
  if (!cleanCode) {
    throw new DiscountApiError('Debes ingresar un código de descuento.', 400);
  }

  const safeCurrency = sanitizeCurrency(params.currency);
  const safeVbucks = Number.isFinite(params.total_vbucks) ? Math.max(0, Math.round(params.total_vbucks)) : 0;

  if (safeVbucks <= 0) {
    throw new DiscountApiError('Agrega objetos a tu carrito antes de aplicar un código.', 400);
  }

  const timeoutMs = options.timeoutMs ?? 8000;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  if (options.signal) {
    if (options.signal.aborted) {
      clearTimeout(timeoutId);
      throw new DOMException('Aborted', 'AbortError');
    }
    options.signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  // Retrieve optional auth token if user is logged in
  let token: string | null = null;
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      token = window.localStorage.getItem('fn_token');
    }
  } catch {
    // Graceful fallback
  }

  const payload: Record<string, any> = {
    code: cleanCode,
    currency: safeCurrency,
    total_vbucks: safeVbucks,
  };

  if (params.customer_username && params.customer_username.trim()) {
    payload.customer_username = params.customer_username.trim();
  }
  if (params.customer_account_id && params.customer_account_id.trim()) {
    payload.customer_account_id = params.customer_account_id.trim();
  }

  try {
    const response = await fetch('/api/discounts/validate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      let errorMessage = 'Código de descuento no válido.';
      let errorDetail = '';

      try {
        const errorJson = await response.json();
        if (errorJson && errorJson.detail) {
          errorDetail = typeof errorJson.detail === 'string' ? errorJson.detail : JSON.stringify(errorJson.detail);
          errorMessage = errorDetail;
        }
      } catch {
        if (response.status === 429) {
          errorMessage = 'Demasiados intentos. Espera un momento antes de volver a intentar.';
        } else if (response.status === 404) {
          errorMessage = `El cupón '${cleanCode}' no existe o es inválido.`;
        }
      }

      throw new DiscountApiError(errorMessage, response.status, errorDetail);
    }

    const data = await response.json();
    return {
      valid: Boolean(data.valid),
      code: String(data.code || cleanCode),
      discount_pct: Number(data.discount_pct || 0),
      discount_amount: Number(data.discount_amount || 0),
      base_price: Number(data.base_price || 0),
      final_price: Number(data.final_price || 0),
      currency: sanitizeCurrency(data.currency || safeCurrency),
      message: String(data.message || `¡Cupón ${cleanCode} aplicado con éxito!`),
    };
  } catch (err: any) {
    clearTimeout(timeoutId);

    if (err?.name === 'AbortError') {
      throw new DiscountApiError('Tiempo de espera agotado al validar el cupón.', 408);
    }
    if (err instanceof DiscountApiError) {
      throw err;
    }
    throw new DiscountApiError(err?.message || 'Error de conexión al validar el cupón.');
  }
}

export default {
  validateCoupon,
};
